/**
 * Step 2 - Miejsca publikacji
 * Fanpage (required) + Grupy FB (min 1 = proceed, 5+ = quality goal)
 * 
 * PREMIUM VERSION V3 - Better hierarchy, clearer actions, polished inputs
 */
import React, { useState } from 'react';
import { Plus, X, Globe, Users, Check, ExternalLink, Info, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function StepPlaces({ value = {}, onChange }) {
  const fanpage = value.fanpage || { name: '', url: '' };
  const groups = value.groups || [];
  
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupUrl, setNewGroupUrl] = useState('');
  
  const updateFanpage = (field, val) => {
    onChange({
      ...value,
      fanpage: { ...fanpage, [field]: val }
    });
  };
  
  const addGroup = () => {
    if (!newGroupName.trim()) return;
    
    const newGroup = {
      id: Date.now().toString(),
      name: newGroupName.trim(),
      url: newGroupUrl.trim(),
      isActive: true
    };
    
    onChange({
      ...value,
      groups: [...groups, newGroup]
    });
    
    setNewGroupName('');
    setNewGroupUrl('');
  };
  
  const removeGroup = (id) => {
    onChange({
      ...value,
      groups: groups.filter(g => g.id !== id)
    });
  };
  
  const activeGroupsCount = groups.filter(g => g.isActive !== false).length;
  const hasFanpage = fanpage.name?.trim();
  const hasMinGroups = activeGroupsCount >= 1;
  const hasRecommendedGroups = activeGroupsCount >= 5;
  
  const progressPercent = Math.min((activeGroupsCount / 5) * 100, 100);
  
  return (
    <div className="space-y-8">
      {/* HEADER - Better hierarchy */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Gdzie będziesz publikować?
        </h1>
        <p className="text-base text-gray-600">
          Dodaj miejsca, w których są Twoi potencjalni klienci.
        </p>
      </div>
      
      {/* FANPAGE SECTION */}
      <div className={`
        bg-white rounded-2xl p-6 transition-all duration-300
        ${hasFanpage 
          ? 'border-2 border-green-400 shadow-lg shadow-green-100/50' 
          : 'border-2 border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300'
        }
      `}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`
              w-12 h-12 rounded-xl flex items-center justify-center transition-all
              ${hasFanpage 
                ? 'bg-green-500 shadow-lg shadow-green-300/50' 
                : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-md shadow-blue-300/30'
              }
            `}>
              {hasFanpage ? (
                <Check className="w-6 h-6 text-white" strokeWidth={3} />
              ) : (
                <Globe className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Fanpage Facebook</h2>
              <span className="inline-block mt-0.5 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                Wymagane
              </span>
            </div>
          </div>
          
          {hasFanpage && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-sm font-bold rounded-full">
              <CheckCircle2 className="w-4 h-4" />
              Gotowe
            </div>
          )}
        </div>
        
        {/* Inputs */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nazwa fanpage'a <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="np. Hodowla Złote Łapy"
              value={fanpage.name}
              onChange={(e) => updateFanpage('name', e.target.value)}
              className="h-12 text-base rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 placeholder:text-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Link do fanpage'a <span className="text-gray-400 font-normal">(opcjonalnie)</span>
            </label>
            <Input
              placeholder="https://facebook.com/twoja-strona"
              value={fanpage.url}
              onChange={(e) => updateFanpage('url', e.target.value)}
              className="h-12 text-base rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 placeholder:text-gray-400"
            />
          </div>
        </div>
      </div>
      
      {/* GROUPS SECTION */}
      <div className={`
        bg-white rounded-2xl p-6 transition-all duration-300
        ${hasRecommendedGroups 
          ? 'border-2 border-green-400 shadow-lg shadow-green-100/50' 
          : hasMinGroups 
            ? 'border-2 border-amber-400 shadow-lg shadow-amber-100/50' 
            : 'border-2 border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300'
        }
      `}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`
              w-12 h-12 rounded-xl flex items-center justify-center transition-all
              ${hasRecommendedGroups 
                ? 'bg-green-500 shadow-lg shadow-green-300/50' 
                : hasMinGroups
                  ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-md shadow-amber-300/30'
                  : 'bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shadow-orange-300/30'
              }
            `}>
              {hasRecommendedGroups ? (
                <Check className="w-6 h-6 text-white" strokeWidth={3} />
              ) : (
                <Users className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Grupy Facebook</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Min. 1 do przejścia • Cel: 5+ grup
              </p>
            </div>
          </div>
          
          {/* Badge - amber for partial, green for complete */}
          <div className={`
            px-3 py-1.5 text-sm font-bold rounded-full transition-colors
            ${hasRecommendedGroups 
              ? 'bg-green-100 text-green-700' 
              : hasMinGroups 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-gray-100 text-gray-500'
            }
          `}>
            {activeGroupsCount} / 5
          </div>
        </div>
        
        {/* PROGRESS BAR */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">
              Postęp
            </span>
            {hasMinGroups && !hasRecommendedGroups && (
              <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                Możesz przejść dalej
              </span>
            )}
            {hasRecommendedGroups && (
              <span className="text-xs font-semibold text-green-600 bg-green-100 px-2.5 py-1 rounded-full">
                ✓ Cel osiągnięty!
              </span>
            )}
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 rounded-full ${
                hasRecommendedGroups 
                  ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                  : hasMinGroups 
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500' 
                    : 'bg-gray-300'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {hasRecommendedGroups 
              ? 'Świetnie! Więcej grup = większy zasięg Twoich postów.' 
              : hasMinGroups 
                ? `Dodaj jeszcze ${5 - activeGroupsCount} grup dla lepszego efektu.` 
                : 'Dodaj min. 1 grupę, żeby przejść dalej.'
            }
          </p>
        </div>
        
        {/* Groups list */}
        {groups.length > 0 && (
          <div className="space-y-2 mb-6">
            {groups.map((group) => (
              <div
                key={group.id}
                className={`
                  flex items-center justify-between p-4 rounded-xl border-2 transition-all
                  ${group.isActive !== false 
                    ? 'bg-gray-50 border-gray-200 hover:border-gray-300' 
                    : 'bg-gray-50 border-gray-100 opacity-50'
                  }
                `}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 truncate">{group.name}</span>
                  {group.url && (
                    <a 
                      href={group.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-orange-600 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <button
                  onClick={() => removeGroup(group.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        {/* ADD GROUP FORM - Clearer action */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-5 border-2 border-dashed border-gray-200">
          <div className="flex gap-3 mb-4">
            <Input
              placeholder="Nazwa grupy"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="flex-1 h-11 text-base rounded-xl border-2 border-gray-200 bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
              onKeyDown={(e) => e.key === 'Enter' && addGroup()}
            />
            <Input
              placeholder="Link (opcjonalnie)"
              value={newGroupUrl}
              onChange={(e) => setNewGroupUrl(e.target.value)}
              className="flex-1 h-11 text-base rounded-xl border-2 border-gray-200 bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
              onKeyDown={(e) => e.key === 'Enter' && addGroup()}
            />
          </div>
          <Button
            type="button"
            onClick={addGroup}
            disabled={!newGroupName.trim()}
            className={`
              w-full h-12 text-base font-semibold rounded-xl transition-all
              ${newGroupName.trim() 
                ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-md shadow-orange-300/30 hover:shadow-lg' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            <Plus className="w-5 h-5 mr-2" />
            Dodaj grupę
          </Button>
        </div>
        
        {/* Info tip */}
        {!hasRecommendedGroups && (
          <div className="flex items-start gap-4 mt-6 p-4 bg-orange-50/50 rounded-xl border border-orange-100">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-orange-800 mb-0.5">
                Dlaczego 5+ grup?
              </p>
              <p className="text-sm text-orange-700 leading-relaxed">
                Możesz przejść dalej już przy 1 grupie, ale najlepsze efekty zwykle są od 5+ miejsc publikacji.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
